import {
  Stack,
  Text,
  Image,
  AspectRatio,
  TextInput,
  Center,
  ActionIcon,
} from "@mantine/core"
import { TextEditor } from "../../../-components/TextEditor"
import {
  DefaultModal,
  DefaultModalProps,
} from "../../../../../-components/default-modal"
import { isEmptyOrWhitespace } from "../../../../../utils/isEmptyOrWhitespace"
import { stripHtml } from "../../../../../utils/stripHtml"
import { useTranslation } from "react-i18next"
import { useChatPostAttachmentApi } from "@/attachment-api/chatPostAttachmentApi"
import { showErrorNotification } from "@/utils/form-submission-notifications"
import { IconPhotoOff, IconX } from "@tabler/icons-react"
import {
  ImageMIMETypes,
  useDefaultFileDialog,
} from "@/routes/-components/useDefaultFileDialog"
import { useForm } from "@mantine/form"
import { CreateChatPost } from "@/@client/Theasoft_ApiServer_Chat"
import { CancelButton, SubmitButton } from "@components/Buttons"
import { FormButtonGroup } from "@components/form-button-group"
import { useChatCreateChatPost } from "@/orval/gen"

interface Props extends DefaultModalProps {
  chatId: number
}

export function NewPostModal({ chatId, ...props }: Props) {
  const { t } = useTranslation("app_server")

  const { addAttachmentAndMetadata } = useChatPostAttachmentApi()
  const form = useForm<Omit<CreateChatPost, "chatId">>({
    mode: "uncontrolled",
    initialValues: { message: "", title: "", replyToMessageId: null },
  })

  const { mutateAsync: sendMessage } = useChatCreateChatPost({
    mutation: {
      onError: showErrorNotification,
      onSuccess: async (id) => {
        if (file) {
          try {
            await addAttachmentAndMetadata(file, { chatPostId: id }, id)
          } catch (err) {
            if (err as Error) {
              showErrorNotification(err as Error)
            }
            console.error(err)
          }
        }
        props.onClose()
      },
    },
  })

  const handleSubmit = async (formValues: typeof form.values) => {
    await sendMessage({ data: { chatId, ...formValues } })
  }

  const { files, fileDialog, setFiles } = useDefaultFileDialog({
    acceptedMIMETypes: ImageMIMETypes,
    multiple: false,
    maxSize: 10,
  })

  const file = files?.at(0)

  return (
    <DefaultModal title={t("NewPost")} {...props}>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <TextInput
            maxLength={150}
            label={t("Title")}
            {...form.getInputProps("title")}
            key={form.key("title")}
          />
          <AspectRatio
            ratio={16 / 9}
            onClick={fileDialog.open}
            style={{ cursor: "pointer" }}
            pos={"relative"}
          >
            {file && (
              <ActionIcon
                pos={"absolute"}
                style={{ zIndex: 9999 }}
                top={0}
                right={0}
                w={"auto"}
                onClick={(e) => {
                  e.stopPropagation()
                  setFiles(null)
                }}
              >
                <IconX />
              </ActionIcon>
            )}
            {file ? (
              <Image
                fit="contain"
                w="100%"
                src={URL.createObjectURL(file)}
                alt={file.name}
              />
            ) : (
              <Center>
                <IconPhotoOff />
                <Text>{t("CoverImage")}</Text>
              </Center>
            )}
          </AspectRatio>
          <TextEditor
            showEmojiPicker
            {...form.getInputProps("message")}
            value={form.getValues().message}
            key={form.key("message")}
          />
          <FormButtonGroup>
            <CancelButton onClick={props.onClose} />
            <SubmitButton
              disabled={isEmptyOrWhitespace(
                stripHtml(form.getValues().message)
              )}
            >
              {t("CreatePost")}
            </SubmitButton>
          </FormButtonGroup>
        </Stack>
      </form>
    </DefaultModal>
  )
}
